import { notifyManager, QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Seed one cache entry per item of a list response — in O(items), not
 * O(items × cache size).
 *
 * Five list hooks prime their detail keys from the list they just fetched
 * (`useListCoachs`, `useGetEvents`, `useListProducts`, `useListArticles`,
 * `useListSponsors`), so a detail page opened from the list paints at once.
 * They did it with one `queryClient.setQueryData` per item, and in React Query
 * v4 that call starts with `queryCache.find`, a LINEAR scan that re-hashes the
 * key of every cached query. The loop is therefore quadratic in the cache, and
 * the cache is shared: after the users roster of a large organization had been
 * loaded once (thousands of per-user entries that live their 5-minute
 * `cacheTime`), every one of these loops on every other page paid
 * items × thousands of key hashes on each organization switch. That is how a
 * freeze that started on the users page followed a platform admin onto pages
 * that never show a user.
 *
 * `QueryCache.build` looks the query up by its hash (O(1)) and creates it when
 * missing — the same path `setQueryData` takes AFTER its scan — and
 * `Query.setData(…, { manual: true })` is exactly what `setQueryData` ends
 * with, so observers of a primed key see the same update they always did. The
 * whole batch notifies once.
 */
export function primeQueryCache<TItem>(
    queryClient: QueryClient,
    keyOf: (item: TItem) => QueryKey,
    items: readonly TItem[] | undefined,
): void {
    if (!items || items.length === 0) return;
    const cache = queryClient.getQueryCache();
    notifyManager.batch(() => {
        for (const item of items) {
            const options = queryClient.defaultQueryOptions({ queryKey: keyOf(item) });
            cache.build(queryClient, options).setData(item, { manual: true });
        }
    });
}
